import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { getBranchesThunk } from "../../../../store/thunks/branchThunks";
import { toast } from "react-toastify";

// Import tab components
import PolicyTab from "./tabs/PolicyTab";
import HybridTab from "./tabs/HybridTab";
import FallbackTab from "./tabs/FallbackTab";
import AdjustmentsTab from "./tabs/AdjustmentsTab";
import MarginsTab from "./tabs/MarginsTab";
import SyncTab from "./tabs/SyncTab";

// Import hooks
import useRateConfig from "./hooks/useRateConfig";

const RateModeConfig = ({ branch }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("policy");

  const {
    rateConfig,
    setRateConfig,
    updateMarginOverride,
    updateHybridMetalPolicy,
    updateFallbackOrder,
    handleSave,
  } = useRateConfig(branch, setLoading);

  useEffect(() => {
    dispatch(getBranchesThunk({ isActive: true }));
  }, [dispatch]);

  const tabs = [
    { id: "policy", label: "Policy", enabled: true },
    { 
      id: "hybrid", 
      label: "Hybrid Settings", 
      enabled: rateConfig.ratePolicy === "HYBRID" 
    },
    { id: "fallback", label: "Fallback Config", enabled: true },
    { id: "adjustments", label: "Adjustments", enabled: true },
    { id: "margins", label: "Margins", enabled: true },
    { id: "sync", label: "Auto Sync", enabled: true },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "policy":
        return (
          <PolicyTab 
            rateConfig={rateConfig} 
            setRateConfig={setRateConfig} 
          />
        );
      case "hybrid":
        return rateConfig.ratePolicy === "HYBRID" ? (
          <HybridTab
            rateConfig={rateConfig}
            setRateConfig={setRateConfig}
            updateHybridMetalPolicy={updateHybridMetalPolicy}
          />
        ) : null;
      case "fallback":
        return (
          <FallbackTab
            rateConfig={rateConfig}
            setRateConfig={setRateConfig}
            updateFallbackOrder={updateFallbackOrder}
          />
        );
      case "adjustments":
        return (
          <AdjustmentsTab
            rateConfig={rateConfig}
            setRateConfig={setRateConfig}
            disabled={rateConfig.ratePolicy === "INHERIT"}
          />
        );
      case "margins":
        return (
          <MarginsTab
            rateConfig={rateConfig}
            setRateConfig={setRateConfig}
            updateMarginOverride={updateMarginOverride}
            disabled={rateConfig.ratePolicy === "INHERIT"}
            hybridConfig={rateConfig.hybridConfig}
          />
        );
      case "sync":
        return (
          <SyncTab
            rateConfig={rateConfig}
            setRateConfig={setRateConfig}
            rateMode={rateConfig.rateMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex -mb-px space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } ${!tab.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={!tab.enabled}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Save Button */}
      <div className="flex justify-between items-center pt-4 border-t">
        <p className="text-sm text-gray-500">
          {rateConfig.ratePolicy === "INHERIT" &&
            "Some settings are disabled when using INHERIT policy"}
          {rateConfig.ratePolicy === "HYBRID" &&
            "Configure metal-specific policies in the Hybrid Settings tab"}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={() => handleSave(branch._id)}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RateModeConfig;
